import { useMemo, useState } from 'react';
import { PartyPopper, ChevronRight } from 'lucide-react';
import { ServiceTile } from '../awards/AwardBadge';
import { metalForYears } from '../awards/awardMetals';

/**
 * Full-width ledger row — one person per row, spanning the whole tab now
 * that anniversaries has its own page instead of a squeezed side column.
 */
function AnniversaryLedgerRow({ employee, years, photos, onSelectEmployee }) {
  const photo = photos?.[employee.id] || employee.openAssetPhoto;
  const initials = (employee.displayName || '?')
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelectEmployee?.(employee)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelectEmployee?.(employee);
        }
      }}
      className="w-full flex items-center gap-3 pl-4 pr-3 py-2.5 border-b border-line last:border-b-0 cursor-pointer
                 transition-colors hover:bg-surface-2"
    >
      {photo ? (
        <img src={photo} alt="" className="w-10 h-10 rounded-full object-cover object-top flex-none" />
      ) : (
        <span className="w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-bold flex-none bg-accent-soft text-accent-ink">
          {initials}
        </span>
      )}

      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-semibold text-ink truncate" title={employee.displayName}>
          {employee.displayName}
        </p>
        <p className="text-[11.5px] text-ink-3 truncate mt-0.5" title={employee.jobTitle}>
          {employee.jobTitle || '—'}
        </p>
      </div>

      <ServiceTile years={years} size={40} title={`${years} ${years === 1 ? 'year' : 'years'}`} />
    </div>
  );
}

/**
 * Card-view tile — same metal-gradient treatment the old compact side-panel
 * card used, sized for a grid tile now that it's reused here instead of a
 * 340px column. Mirrors the directory's own List/Card toggle so the two tabs
 * share one set of view controls instead of each inventing its own.
 */
function AnniversaryCard({ employee, years, photos, onSelectEmployee }) {
  const photo = photos?.[employee.id] || employee.openAssetPhoto;
  const metal = metalForYears(years);
  const initials = (employee.displayName || '?')
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelectEmployee?.(employee)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelectEmployee?.(employee);
        }
      }}
      className="relative overflow-hidden flex items-center gap-2.5 p-3 rounded-ctl border border-line-strong cursor-pointer
                 transition-[background,border-color]
                 bg-[radial-gradient(120%_60%_at_12%_0%,var(--metal-sheen),transparent_55%),linear-gradient(135deg,color-mix(in_srgb,var(--rail-ring)_16%,var(--surface))_0%,color-mix(in_srgb,var(--rail-ring)_5%,var(--surface))_50%,color-mix(in_srgb,var(--rail-ring)_16%,var(--surface))_100%)]
                 hover:border-[var(--rail-ring)]
                 hover:bg-[radial-gradient(120%_60%_at_12%_0%,var(--metal-sheen),transparent_55%),linear-gradient(135deg,color-mix(in_srgb,var(--rail-ring)_26%,var(--surface))_0%,color-mix(in_srgb,var(--rail-ring)_9%,var(--surface))_50%,color-mix(in_srgb,var(--rail-ring)_26%,var(--surface))_100%)]"
      style={{
        '--rail-ring': `var(--${metal.base}-ring)`,
        '--metal-sheen': `var(--${metal.base}-sheen, var(--sheen))`,
      }}
    >
      {photo ? (
        <img src={photo} alt="" className="w-10 h-10 rounded-full object-cover object-top flex-none" />
      ) : (
        <span className="w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-bold flex-none bg-accent-soft text-accent-ink">
          {initials}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-ink truncate" title={employee.displayName}>
          {employee.displayName}
        </p>
        <p className="text-[11px] text-ink-3 truncate mt-0.5" title={employee.jobTitle}>
          {employee.jobTitle || '—'}
        </p>
      </div>
      <ServiceTile years={years} size={32} title={`${years} ${years === 1 ? 'year' : 'years'}`} />
    </div>
  );
}

/**
 * Full-width Anniversaries tab content — the Directory tab's sibling.
 * Groups by exact year descending, with a sticky per-year header matching
 * the directory list's sticky A/B/C letter bands. Collapsible per year
 * (default expanded — this is the one place the whole month is meant to be
 * visible at a glance), plus an expand/collapse-all shortcut for busy months.
 */
export function AnniversariesView({ anniversaries, photos, onSelectEmployee, displayMode = 'list' }) {
  const [collapsedYears, setCollapsedYears] = useState(() => new Set());

  const groups = useMemo(() => {
    const map = new Map();
    for (const entry of anniversaries) {
      if (!map.has(entry.years)) map.set(entry.years, []);
      map.get(entry.years).push(entry);
    }
    return Array.from(map.entries()).sort((a, b) => b[0] - a[0]);
  }, [anniversaries]);

  const toggleYear = (years) => {
    setCollapsedYears((prev) => {
      const next = new Set(prev);
      if (next.has(years)) next.delete(years);
      else next.add(years);
      return next;
    });
  };

  const allExpanded = collapsedYears.size === 0;
  const toggleAll = () => {
    setCollapsedYears(allExpanded ? new Set(groups.map(([years]) => years)) : new Set());
  };

  return (
    <div className="w-full">
      <div className="rounded-card overflow-hidden bg-surface border border-line shadow-e1">
        {anniversaries.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-4 py-16 text-center">
            <PartyPopper size={22} className="text-ink-4" />
            <p className="text-[13px] text-ink-3">No work anniversaries this month</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-surface-2 border-b border-line">
              <span className="text-[12px] text-ink-3">
                <b className="font-mono font-semibold text-ink-2">{anniversaries.length}</b>
                {' '}anniversaries this month
              </span>
              {groups.length > 0 && (
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-[11.5px] font-semibold text-accent hover:text-accent-ink"
                >
                  {allExpanded ? 'Collapse all' : 'Expand all'}
                </button>
              )}
            </div>

            {groups.map(([years, items]) => {
              const isOpen = !collapsedYears.has(years);
              return (
                <div key={years}>
                  <button
                    type="button"
                    onClick={() => toggleYear(years)}
                    aria-expanded={isOpen}
                    className="w-full sticky top-0 z-[5] flex items-center gap-2.5 px-4 py-2 bg-surface-2 border-b border-line text-left transition-colors hover:bg-accent-soft"
                  >
                    <ChevronRight
                      size={13}
                      className={`flex-none text-ink-4 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                    />
                    <span className="text-[12px] font-extrabold tracking-[0.04em] text-accent-ink">
                      {years} {years === 1 ? 'Year' : 'Years'}
                    </span>
                    <span className="font-mono text-[10.5px] text-ink-4">{items.length}</span>
                  </button>
                  {isOpen && (
                    displayMode === 'card' ? (
                      <div className="grid gap-3 p-3 grid-cols-[repeat(auto-fill,minmax(212px,1fr))]">
                        {items.map(({ employee, years: personYears }) => (
                          <AnniversaryCard
                            key={employee.id}
                            employee={employee}
                            years={personYears}
                            photos={photos}
                            onSelectEmployee={onSelectEmployee}
                          />
                        ))}
                      </div>
                    ) : (
                      items.map(({ employee, years: personYears }) => (
                        <AnniversaryLedgerRow
                          key={employee.id}
                          employee={employee}
                          years={personYears}
                          photos={photos}
                          onSelectEmployee={onSelectEmployee}
                        />
                      ))
                    )
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
